import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processSession, login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [showEmergency, setShowEmergency] = useState(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      // IMPORTANT: Do NOT use URLSearchParams for hash fragments - it converts '+' to spaces
      const hash = window.location.hash.substring(1); // remove '#'
      let sessionId = null;
      for (const part of hash.split('&')) {
        const eqIdx = part.indexOf('=');
        if (eqIdx !== -1 && part.substring(0, eqIdx) === 'session_id') {
          sessionId = decodeURIComponent(part.substring(eqIdx + 1));
          break;
        }
      }

      if (!sessionId) {
        console.error('No session_id found in URL');
        navigate('/login', { replace: true });
        return;
      }

      // Backend handles retries internally - single attempt with generous timeout
      try {
        const userData = await processSession(sessionId);
        
        // Clear the hash from URL to prevent re-processing
        window.history.replaceState(null, '', window.location.pathname);
        
        // Navigate to dashboard with user data
        navigate('/dashboard', { 
          replace: true, 
          state: { user: userData } 
        });
        return;
      } catch (err) {
        console.error('Auth processing failed:', err);
        // All attempts failed - show error instead of looping back to login
        window.history.replaceState(null, '', window.location.pathname);
        setError(err?.message || 'Authentication failed');
      }
    };

    processAuth();
  }, [location.hash, navigate, processSession]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black" data-testid="auth-error-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-xl tracking-widest mb-4 font-mono">
            AUTHENTICATION FAILED
          </div>
          <p className="text-gray-400 mb-6 text-sm font-mono">
            Session expired or could not be verified. This can happen if the page took too long to load.
          </p>
          <div className="flex gap-4 justify-center mb-6">
            <button
              data-testid="auth-retry-btn"
              onClick={() => login()}
              className="px-6 py-2 bg-[#00FF41] text-black font-mono tracking-wider hover:bg-[#00CC33] transition-colors"
            >
              TRY AGAIN
            </button>
            <button
              data-testid="auth-back-login-btn"
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2 border border-[#00FF41] text-[#00FF41] font-mono tracking-wider hover:bg-[#003B00] transition-colors"
            >
              BACK TO LOGIN
            </button>
          </div>
          
          {!showEmergency ? (
            <button
              data-testid="show-emergency-btn"
              onClick={() => setShowEmergency(true)}
              className="text-gray-600 text-xs font-mono hover:text-gray-400 transition-colors underline"
            >
              Admin Emergency Access
            </button>
          ) : (
            <form 
              method="POST" 
              action={`${process.env.REACT_APP_BACKEND_URL}/api/v2/auth/direct-login`}
              className="mt-4 p-4 border border-gray-700 rounded space-y-3" 
              data-testid="emergency-form"
            >
              <input
                data-testid="emergency-email"
                type="email"
                name="email"
                placeholder="Admin email"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white font-mono text-sm rounded"
              />
              <input
                data-testid="emergency-code"
                type="password"
                name="access_code"
                placeholder="Access code"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white font-mono text-sm rounded"
              />
              <button
                type="submit"
                data-testid="emergency-submit-btn"
                className="w-full px-4 py-2 bg-yellow-600 text-black font-mono text-sm tracking-wider hover:bg-yellow-500 transition-colors"
              >
                EMERGENCY LOGIN
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading-screen">
      <div className="text-center">
        <div className="animate-pulse text-[#00FF41] text-xl tracking-widest mb-4">
          AUTHENTICATING...
        </div>
        <div className="w-48 h-1 bg-[#003B00] mx-auto overflow-hidden">
          <div className="h-full bg-[#00FF41] animate-loading-bar"></div>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
